// eslint-disable-next-line no-restricted-imports
import { auto } from "angular";
import {
  LazyLoadResult,
  NATIVE_INJECTOR_TOKEN,
  Ng2StateDeclaration,
  StateDeclaration,
  StateRegistry,
  StatesModule,
  Transition,
  UIRouter,
  applyNgModule,
} from "@uirouter/angular";
// eslint-disable-next-line no-restricted-imports
import { Ng1StateDeclaration } from "@uirouter/angularjs";
import { Injector, NgModuleRef } from "@angular/core";
import { downgradeModule } from "@angular/upgrade/static";
import { noop } from "rxjs";

const INJECTOR_STATE = "__injector_state";

/**
 * UI router does not support child future states and wrongly generate the URL. To solve this we can insert
 * abstract states, but they need to be replaced with a future state before we load the async module.
 *
 * #### Example:
 * ```js
 * // if we have those states
 * const states1 = [
 *     { name: "gtmhub.automation.**", url: "/automation/" },
 *     { name: "gtmhub.automation.history.**", url: "/automation/history/:automationId/" },
 * ];
 *
 * // and try to generate a URL to the second one via either ui-sref directive or $state.href method
 * // we will always hit the first state and the URL will be incomplete
 * console.log($state.href("gtmhub.automation.history", { automationId: "1" })); // will print "/automation/"
 *
 * // To solve this we can register the first state as abstract with its original name
 * const states2 = [
 *     { name: "gtmhub.automation", abstract: true },
 *     { name: "gtmhub.automation.history.**", url: "/automation/history/:automationId/" },
 * ];
 *
 * // then we will have the correct URL
 * ```
 */
export function replaceWithFutureStatesConfig(states: string[]): StatesModule["config"] {
  return (uiRouterInstance: UIRouter): void => {
    const { stateRegistry } = uiRouterInstance;
    for (const state of states) {
      stateRegistry.deregister(state);
      stateRegistry.register({ name: `${state}.**` });
    }
  };
}

let currentlyDowngradedModule: string = undefined;

export const getCurrentlyDowngradedModule = (): string | undefined => currentlyDowngradedModule;

export type LazyNgAndNg1ModuleRef = LazyModuleRef & {
  getNg1Module(): Promise<string>;
};

type LazyModuleRef = {
  lazyState: string;
  createModuleRef(parentInjector: Injector): NgModuleRef<unknown>;
  getModuleInjector(): Injector;
};

const setInjectorToChildStates = (stateRegistry: StateRegistry): void => {
  for (const state of stateRegistry.get()) {
    const injectorState = state.data?.[INJECTOR_STATE];
    if (injectorState) {
      delete state.data[INJECTOR_STATE];

      const injectorResolable = stateRegistry
        .get(injectorState)
        .$$state()
        .resolvables.find((x) => x.token === NATIVE_INJECTOR_TOKEN);
      state.$$state().resolvables.push(injectorResolable);
    }
  }
};

export function lazyLoadNgAndNg1Module(
  lazyModuleRefFactory: () => Promise<LazyNgAndNg1ModuleRef>
): (transition: Transition, stateObject: StateDeclaration) => Promise<LazyLoadResult> {
  return (transition: Transition, stateObject: StateDeclaration) => {
    const routerInjector = transition.injector();
    const $injector = routerInjector.get<auto.IInjectorService>("$injector");
    const $ocLazyLoad = routerInjector.get<oc.ILazyLoad>("$ocLazyLoad");
    const injector = routerInjector.get<Injector>(NATIVE_INJECTOR_TOKEN);

    return lazyModuleRefFactory().then((mod) => {
      const moduleRef = mod.createModuleRef(injector);
      const lazyLoadResult = applyNgModule(transition, moduleRef, injector, stateObject);

      const stateRegistry = injector.get(StateRegistry);
      setInjectorToChildStates(stateRegistry);

      const downgradedModule = downgradeModule(() => Promise.resolve(moduleRef));
      currentlyDowngradedModule = downgradedModule;

      return $ocLazyLoad
        .inject(downgradedModule)
        .then(() => mod.getNg1Module())
        .then((ng1Module) => {
          currentlyDowngradedModule = undefined;

          // trigger the completion of the lazy load promise
          $injector.invoke([`$$angularLazyModuleRef${downgradedModule}`, noop]);

          return $ocLazyLoad.inject(ng1Module).then(() => lazyLoadResult);
        });
    });
  };
}

export function lazyLoadNgModule(lazyModuleRefFactory: () => Promise<LazyModuleRef>): (transition: Transition, stateObject: StateDeclaration) => Promise<LazyLoadResult> {
  return (transition: Transition, stateObject: StateDeclaration) => {
    const routerInjector = transition.injector();
    const injector = routerInjector.get<Injector>(NATIVE_INJECTOR_TOKEN);

    return lazyModuleRefFactory().then((mod) => {
      const moduleRef = mod.createModuleRef(injector);
      const lazyLoadResult = applyNgModule(transition, moduleRef, injector, stateObject);

      const stateRegistry = injector.get(StateRegistry);
      setInjectorToChildStates(stateRegistry);

      return lazyLoadResult;
    });
  };
}

type ChildFutureStateRegistration = {
  base: string;
  factory: string;
};

type StatesFactory = (base: string) => (Ng1StateDeclaration | Ng2StateDeclaration)[];

let childFutureStateRegistrations: ChildFutureStateRegistration[] = [];
const childFutureStateFactories: Record<string, StatesFactory> = {};
const stateFactoryToRootModuleState: Record<string, string> = {};

export const registerChildFutureState = (registration: ChildFutureStateRegistration): void => {
  childFutureStateRegistrations.push(registration);
};

/**
 * Registers a set of state factories to be called when lazy-loading other modules.
 *
 * @param factories
 * @param rootModuleState A state that will contain the injector for that module. Usually, all root states do this, so just pass one
 */
export const registerChildFutureStateFactories = (factories: Record<string, StatesFactory>, rootModuleState: string): void => {
  for (const [key, factory] of Object.entries(factories)) {
    childFutureStateFactories[key] = factory;
    stateFactoryToRootModuleState[key] = rootModuleState;
  }
};

const filterRootStates = (states: StateDeclaration[]): StateDeclaration[] =>
  states.reduce<StateDeclaration[]>((result, state) => {
    const nonChildStates = result.filter((x) => !x.name.startsWith(`${state.name}.`));
    if (nonChildStates.length !== result.length) {
      result = nonChildStates;
    }

    if (!result.some((x) => state.name.startsWith(`${x.name}.`))) {
      result.push(state);
    }

    return result;
  }, []);

export function loadChildFutureStatesConfig(uiRouter: UIRouter, injector: Injector, module: StatesModule): void {
  const unprocessedChildFutureStateRegistrations: ChildFutureStateRegistration[] = [];

  for (const registration of childFutureStateRegistrations) {
    const factory = childFutureStateFactories[registration.factory];
    if (!factory) {
      unprocessedChildFutureStateRegistrations.push(registration);
      continue;
    }

    const states = factory(registration.base) as unknown as Ng2StateDeclaration[];

    const rootModuleState = stateFactoryToRootModuleState[registration.factory];
    if (rootModuleState) {
      for (const state of filterRootStates(states)) {
        if (!state.data) {
          state.data = {};
        }
        state.data[INJECTOR_STATE] = rootModuleState;
      }
    }

    module.states.push(...states);
  }

  childFutureStateRegistrations = unprocessedChildFutureStateRegistrations;
}
